/**
 * WhatsAppLink.jsx
 *
 * Dashboard view for linking a WhatsApp number to this business account.
 * Reached from the sidebar footer in RoadmapPage.jsx.
 *
 *   GET    /api/whatsapp/status        → linked numbers
 *   POST   /api/whatsapp/link-code     → one-time code (plan-gated: 'whatsapp')
 *   DELETE /api/whatsapp/link/:phone   → unlink
 *
 * The user sends "LINK <code>" to the BusinessRun WhatsApp number
 * (REACT_APP_WHATSAPP_NUMBER, digits only, e.g. 2348012345678).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  MessageCircle, Loader2, AlertCircle, Copy, Check, Trash2, Lock, ArrowRight,
} from 'lucide-react';

const BRAND = '#C5A028';
const WA_NUMBER = (process.env.REACT_APP_WHATSAPP_NUMBER || '').replace(/[^\d]/g, '');

function useCountdown(expiresAt) {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    if (!expiresAt) { setLeft(0); return undefined; }
    const tick = () => setLeft(Math.max(0, Math.round((new Date(expiresAt).getTime() - Date.now()) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);
  return left;
}

export default function WhatsAppLink() {
  const navigate = useNavigate();
  const { subscription } = useAuth();
  const planAllowed = Boolean(subscription?.features?.includes('whatsapp'));

  const [links,      setLinks]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');
  const [generating, setGenerating] = useState(false);
  const [code,       setCode]       = useState(null); // { code, expiresAt }
  const [copied,     setCopied]     = useState(false);
  const [removing,   setRemoving]   = useState(null);
  const [serverNumber, setServerNumber] = useState('');
  const waNumber = serverNumber || WA_NUMBER;

  const secondsLeft = useCountdown(code?.expiresAt);
  const codeLive = code && secondsLeft > 0;

  const loadStatus = useCallback(async () => {
    try {
      const res  = await fetch('/api/whatsapp/status', { credentials: 'include' });
      const data = await res.json();
      if (!res.ok || !data.success) { setError(data.message || 'Could not load WhatsApp status.'); return; }
      setLinks(data.links || []);
      setServerNumber(String(data.whatsappNumber || '').replace(/[^\d]/g, ''));
    } catch {
      setError('Network error loading WhatsApp status.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // While a code is live, poll so the card flips to "linked" once the user sends it.
  useEffect(() => {
    if (!codeLive) return undefined;
    const id = setInterval(loadStatus, 4000);
    return () => clearInterval(id);
  }, [codeLive, loadStatus]);

  const linkedHere = links.some(l => l.thisBusiness);
  useEffect(() => { if (linkedHere) setCode(null); }, [linkedHere]);

  async function generate() {
    setError('');
    setGenerating(true);
    try {
      const res  = await fetch('/api/whatsapp/link-code', { method: 'POST', credentials: 'include' });
      const data = await res.json();
      if (!res.ok || !data.success) { setError(data.message || 'Could not generate a code.'); return; }
      setCode({ code: data.code, expiresAt: data.expiresAt });
      setCopied(false);
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setGenerating(false);
    }
  }

  async function unlink(phone) {
    if (!window.confirm('Unlink this WhatsApp number? It will stop working with BusinessRun.')) return;
    setRemoving(phone);
    try {
      const res  = await fetch(`/api/whatsapp/link/${encodeURIComponent(phone)}`, { method: 'DELETE', credentials: 'include' });
      const data = await res.json();
      if (res.ok && data.success) setLinks(prev => prev.filter(l => l.phone !== phone));
      else setError(data.message || 'Could not unlink that number.');
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setRemoving(null);
    }
  }

  function copyMessage() {
    navigator.clipboard?.writeText(`LINK ${code.code}`).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  const mm = String(Math.floor(secondsLeft / 60)).padStart(1, '0');
  const ss = String(secondsLeft % 60).padStart(2, '0');

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
      <div className="mb-6">
        <h1 className="text-xl font-black uppercase tracking-tight text-zinc-900 flex items-center gap-2">
          <MessageCircle size={20} style={{ color: BRAND }} />
          WhatsApp
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          Run your business from WhatsApp — log sales, check stock and get your numbers by chatting with BusinessRun.
        </p>
      </div>

      {error && (
        <div className="flex items-start gap-2 px-4 py-3 mb-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
          <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-16 text-zinc-400"><Loader2 size={22} className="animate-spin" /></div>
      )}

      {!loading && (
        <div className="space-y-4">
          {links.length > 0 && (
            <div className="bg-white border border-zinc-200 rounded-2xl p-5 space-y-3">
              <p className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Linked numbers</p>
              {links.map(l => (
                <div key={l.phone} className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-black text-zinc-900">{l.phoneMasked}</p>
                    <p className="text-[11px] text-zinc-400">
                      {l.thisBusiness ? 'Linked to this business' : 'Linked to another of your businesses'}
                    </p>
                  </div>
                  <button
                    onClick={() => unlink(l.phone)}
                    disabled={removing === l.phone}
                    title="Unlink"
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-zinc-400 hover:text-red-500 hover:bg-red-50 transition disabled:opacity-40"
                  >
                    {removing === l.phone ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                  </button>
                </div>
              ))}
            </div>
          )}

          {!planAllowed && (
            <div className="flex flex-col items-center text-center py-12 border border-dashed border-zinc-200 rounded-2xl">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3" style={{ background: BRAND + '15' }}>
                <Lock size={20} style={{ color: BRAND }} />
              </div>
              <p className="text-sm font-bold text-zinc-700">WhatsApp is part of the paid plans</p>
              <p className="text-xs text-zinc-400 mt-1 max-w-xs">Upgrade to BusinessRun +1 or higher to chat with your business on WhatsApp.</p>
              <button
                onClick={() => navigate('/billing')}
                className="mt-4 flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black font-black text-xs uppercase tracking-widest px-4 py-3 rounded-xl transition"
              >
                See plans <ArrowRight size={14} />
              </button>
            </div>
          )}

          {planAllowed && !linkedHere && (
            <div className="bg-white border border-zinc-200 rounded-2xl p-6">
              {!codeLive ? (
                <>
                  <p className="text-sm font-bold text-zinc-800">Link your WhatsApp number</p>
                  <p className="text-xs text-zinc-500 mt-1 mb-4">
                    Generate a one-time code. You will then tap a button that opens a WhatsApp chat with BusinessRun, with the message already typed. Just press send from the WhatsApp number you want to link.
                  </p>
                  <button
                    onClick={generate}
                    disabled={generating}
                    className="flex items-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-black font-black text-xs uppercase tracking-widest px-5 py-3.5 rounded-xl transition"
                  >
                    {generating ? <Loader2 size={15} className="animate-spin" /> : <MessageCircle size={15} />}
                    {generating ? 'Generating…' : code ? 'Generate a new code' : 'Generate code'}
                  </button>
                </>
              ) : (
                <>
                  <p className="text-[10px] font-black uppercase tracking-widest text-zinc-500 mb-3">Link in 3 steps</p>
                  <ol className="space-y-5 text-sm text-zinc-700">
                    <li>
                      <p className="font-bold text-zinc-800">1. Open a chat with BusinessRun</p>
                      {waNumber ? (
                        <>
                          <p className="text-xs text-zinc-500 mt-1">
                            Chat with <span className="font-mono font-bold">+{waNumber}</span> on WhatsApp.
                          </p>
                          <a
                            href={`https://wa.me/${waNumber}?text=${encodeURIComponent(`LINK ${code.code}`)}`}
                            target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 mt-3 bg-green-500 hover:bg-green-400 text-black font-black text-xs uppercase tracking-widest px-5 py-3.5 rounded-xl transition"
                          >
                            <MessageCircle size={15} /> Open WhatsApp
                          </a>
                        </>
                      ) : (
                        <p className="text-xs text-zinc-500 mt-1">
                          Open WhatsApp and start a chat with the BusinessRun number.
                        </p>
                      )}
                    </li>
                    <li>
                      <p className="font-bold text-zinc-800">2. Send this exact message</p>
                      <div className="flex items-center justify-between gap-3 bg-zinc-50 border border-zinc-200 rounded-xl px-4 py-4 mt-2">
                        <span className="font-mono text-xl font-black tracking-widest text-zinc-900">LINK {code.code}</span>
                        <button onClick={copyMessage} title="Copy" className="text-zinc-400 hover:text-zinc-800 transition">
                          {copied ? <Check size={18} className="text-green-500" /> : <Copy size={18} />}
                        </button>
                      </div>
                      {waNumber && (
                        <p className="text-xs text-zinc-500 mt-2">The button above types it for you. Just press send.</p>
                      )}
                    </li>
                    <li>
                      <p className="font-bold text-zinc-800">3. Wait for the reply</p>
                      <p className="text-xs text-zinc-500 mt-1">
                        Expires in <span className="font-black text-zinc-800">{mm}:{ss}</span>. Works once.
                      </p>
                    </li>
                  </ol>
                </>
              )}
            </div>
          )}

          {planAllowed && linkedHere && (
            <div className="flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700">
              <Check size={16} className="flex-shrink-0" />
              <span>WhatsApp is connected. Send "hi" to BusinessRun to get started.</span>
              {waNumber && (
                <a
                  href={`https://wa.me/${waNumber}`}
                  target="_blank" rel="noopener noreferrer"
                  className="ml-auto font-black text-xs uppercase tracking-widest underline whitespace-nowrap"
                >
                  Open chat
                </a>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
